import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BlogCard, type BlogPost } from '../components/blog/BlogCard';
import { useAppSettings } from '../contexts/SettingsContext';

// Sample blog posts data - in production, this would come from an API or CMS
const SAMPLE_POSTS: BlogPost[] = [
  {
    slug: 'como-elegir-alimento-perro',
    title: 'Como Elegir el Mejor Alimento para tu Perro',
    excerpt: 'Guia completa para seleccionar la comida adecuada segun la edad, tamano y necesidades especiales de tu mascota. Aprende a leer las etiquetas y entender los ingredientes.',
    imageUrl: '/blog/dog-food.jpg',
    category: 'Nutricion',
    publishedAt: '2026-01-15',
    author: 'Dr. Maria Garcia',
    readingTime: '5 min',
  },
  {
    slug: 'cuidados-gato-invierno',
    title: 'Cuidados Especiales para tu Gato en Invierno',
    excerpt: 'Consejos esenciales para mantener a tu gato saludable durante los meses frios. Desde la alimentacion hasta el acondicionamiento del hogar.',
    imageUrl: '/blog/cat-winter.jpg',
    category: 'Cuidados',
    publishedAt: '2026-01-10',
    author: 'Dra. Ana Lopez',
    readingTime: '4 min',
  },
  {
    slug: 'beneficios-pasear-perro',
    title: 'Los Beneficios de Pasear a tu Perro Diariamente',
    excerpt: 'Descubre por que los paseos diarios son fundamentales para la salud fisica y mental de tu perro, y como establecer una rutina efectiva.',
    imageUrl: '/blog/dog-walk.jpg',
    category: 'Salud',
    publishedAt: '2026-01-05',
    author: 'Carlos Martinez',
    readingTime: '6 min',
  },
  {
    slug: 'vacunas-cachorros-guia',
    title: 'Guia Completa de Vacunas para Cachorros',
    excerpt: 'Todo lo que necesitas saber sobre el calendario de vacunacion de tu cachorro. Vacunas esenciales, refuerzos y recomendaciones.',
    imageUrl: '/blog/puppy-vaccine.jpg',
    category: 'Salud',
    publishedAt: '2025-12-28',
    author: 'Dr. Pedro Sanchez',
    readingTime: '7 min',
  },
  {
    slug: 'juguetes-interactivos-gatos',
    title: 'Los Mejores Juguetes Interactivos para Gatos',
    excerpt: 'Mantener a tu gato activo y mentalmente estimulado es crucial. Te presentamos los juguetes mas efectivos para evitar el aburrimiento.',
    imageUrl: '/blog/cat-toys.jpg',
    category: 'Tips',
    publishedAt: '2025-12-20',
    author: 'Laura Fernandez',
    readingTime: '4 min',
  },
  {
    slug: 'senales-perro-enfermo',
    title: 'Senales de que tu Perro Podria Estar Enfermo',
    excerpt: 'Aprende a identificar los sintomas que indican que tu perro necesita atencion veterinaria. Senales de alerta que no debes ignorar.',
    imageUrl: '/blog/dog-health.jpg',
    category: 'Salud',
    publishedAt: '2025-12-15',
    author: 'Dr. Maria Garcia',
    readingTime: '5 min',
  },
];

const CATEGORIES = ['Todas', 'Nutricion', 'Salud', 'Cuidados', 'Tips'];

export function BlogPage() {
  const { settings } = useAppSettings();
  const [selectedCategory, setSelectedCategory] = useState('Todas');

  // Check if blog is enabled
  if (settings && !settings.features?.blogEnabled) {
    return (
      <div className="container py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Blog no disponible</h1>
        <p className="mt-2 text-gray-600">
          El blog no esta disponible en este momento.
        </p>
        <Link to="/" className="mt-4 btn btn-primary inline-block">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const filteredPosts = selectedCategory === 'Todas'
    ? SAMPLE_POSTS
    : SAMPLE_POSTS.filter(post => post.category === selectedCategory);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero section */}
      <div className="bg-white border-b">
        <div className="container py-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Blog PetMas
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Consejos, guias y noticias para el cuidado de tu mascota.
            Aprende de nuestros expertos veterinarios y especialistas en bienestar animal.
          </p>
        </div>
      </div>

      {/* Categories */}
      <div className="container py-6">
        <div className="flex flex-wrap gap-2 justify-center">
          {CATEGORIES.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Blog posts grid */}
      <div className="container pb-12">
        {filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map(post => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No hay articulos en esta categoria.</p>
          </div>
        )}
      </div>

      {/* Newsletter signup */}
      <div className="bg-orange-500 py-12">
        <div className="container text-center">
          <h2 className="text-2xl font-bold text-white">
            Suscribete a nuestro newsletter
          </h2>
          <p className="mt-2 text-orange-100 max-w-xl mx-auto">
            Recibe los mejores consejos para el cuidado de tu mascota directamente en tu correo.
          </p>
          <form className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Tu correo electronico"
              className="flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-white text-orange-500 font-medium rounded-lg hover:bg-orange-50 transition-colors"
            >
              Suscribirse
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
